import {
    ActionReducerMapBuilder,
    createAsyncThunk,
    createSlice,
    Draft,
    SerializedError,
} from '@reduxjs/toolkit';
import { urlParamReplicaId, urlProjectWorkflowHistory } from '../../Constants';
import { IAction, SliceProps } from '../store';
import { apiFetch } from '../../util/ProjectUtils';

/**
 * Initial slice state value.
 */
const initialState: IProjectWorkflowHistoryState = {
    data: null,
    loading: false,
    error: null,
};

/**
 * Collection of the reducers for this slice.
 */
const reducers = {};

/**
 * Collection of the thunks for this slice, along with their reducer functions.
 */
const thunks = {
    /**
     * Async function that fetches the workflow history for the target project.
     * @param replicaId - ReplicaId for the target project.
     * @param idDocIndex - idDocIndex for the target project.
     * @returns Promise for the HTTP request.
     */
    getProjectWorkflowHistory: async (config: {
        universalId: string;
        replicaId: string;
    }): Promise<string> => {
        try {
            const { replicaId, universalId } = config;

            const request: RequestInit = {
                headers: {
                    universalId,
                },
            };
            const url = urlProjectWorkflowHistory.replace(urlParamReplicaId, replicaId);
            const response = await apiFetch(url, request);

            return response.ok ? Promise.resolve(response.text()) : Promise.reject();
        } catch (e) {
            return Promise.reject(e as Error);
        }
    },

    /**
     * Registers the 'extraReducers' functions for the slice's thunks.
     */
    _reducers: (builder: ActionReducerMapBuilder<IProjectWorkflowHistoryState>) => {
        const onError = (state: IProjectWorkflowHistoryState, action: IAction<SerializedError>) => {
            state.loading = false;
            state.data = null;
            state.error = action.error;
        };
        /**
         * Reducers for the 'getProjectWorkflowHistory' thunk.
         */
        builder.addCase(
            getProjectWorkflowHistory.fulfilled,
            (state: Draft<IProjectWorkflowHistoryState>, action: IAction<string>) => {
                state.data = action.payload;
                state.loading = false;
                state.error = null;
            },
        );
        builder.addCase(getProjectWorkflowHistory.rejected, onError);
    },
};

/**
 * Expose thunk functions as exports.
 */
export const getProjectWorkflowHistory = createAsyncThunk(
    'projectWorkflowHistory',
    thunks.getProjectWorkflowHistory,
);

/**
 * Interface that describes the project workflow history state.
 */

export interface IProjectWorkflowHistoryState extends SliceProps {
    data: string;
}

/**
 * Create the redux slice.
 */
export const projectWorkflowHistory = createSlice({
    name: 'projectWorkflowHistory',
    extraReducers: thunks._reducers,
    initialState,
    reducers,
});

/**
 * Export slice reducer as default.
 */
export default projectWorkflowHistory.reducer;
